import React, { useMemo } from 'react';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { useThemeMode } from '../../ThemeContext';
import ContactUsForm from './ContactUsForm';
import Checkmark from '../../assets/checkmark.svg';
import { APP_URLS } from '../../helpers/url_helper';
import '../Subscription/Subscription.css';

const ContactSales = () => {
  const { mode } = useThemeMode();
  const location = useLocation();
  const navigate = useNavigate();

  const planTitle = location.state?.planTitle || 'Enterprise Plan';
  const displayPrice = location.state?.displayPrice || 'Custom pricing';
  const billingCycle = location.state?.billingCycle || 'MONTHLY';

  const features = useMemo(() => {
    const formatted = location.state?.formattedDescription;
    if (!formatted) {
      return [];
    }
    return formatted
      .split('\n')
      .slice(1)
      .map(line => line.trim())
      .filter(Boolean);
  }, [location.state?.formattedDescription]);

  return (
    <div
      style={{
        padding: '24px',
        backgroundColor: mode === 'dark' ? '#0f0f0f' : '#f5f7f9',
        minHeight: 'calc(100vh - 120px)',
      }}
    >
      <Container fluid className="px-0">
        <Row className="g-4">
          <Col
            xl={5}
            lg={5}
            md={12}
            style={{
              minWidth: '360px',
            }}
          >
            <Card
              className="h-100"
              style={{
                borderRadius: '16px',
                border: 'none',
                boxShadow: mode === 'dark'
                  ? '0 12px 24px rgba(0, 0, 0, 0.4)'
                  : '0 12px 24px rgba(38, 195, 192, 0.12)',
                backgroundColor: mode === 'dark' ? '#151515' : '#ffffff',
              }}
            >
              <Card.Body style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={{ marginBottom: '16px' }}>
                  <span
                    style={{
                      display: 'inline-block',
                      padding: '6px 14px',
                      borderRadius: '999px',
                      backgroundColor: 'rgba(38, 195, 192, 0.12)',
                      color: '#26C3C0',
                      fontWeight: 600,
                      fontSize: '13px',
                      letterSpacing: '0.4px',
                      textTransform: 'uppercase',
                    }}
                  >
                    Plan Details
                  </span>
                </div>
                <h3
                  style={{
                    color: mode === 'dark' ? '#ffffff' : '#001219',
                    fontWeight: 700,
                    fontSize: '24px',
                    marginBottom: '12px',
                  }}
                >
                  {planTitle}
                </h3>
                <p
                  style={{
                    color: mode === 'dark' ? '#8f9aa3' : '#5c6b73',
                    marginBottom: '24px',
                    lineHeight: 1.6,
                  }}
                >
                  Share your requirements with our team. We’ll craft a tailored
                  compliance solution and pricing to match your organisation’s needs.
                </p>

                <div
                  style={{
                    borderRadius: '14px',
                    padding: '18px',
                    background: mode === 'dark'
                      ? 'linear-gradient(135deg, rgba(38,195,192,0.15), rgba(17,24,39,0.4))'
                      : 'linear-gradient(135deg, rgba(38,195,192,0.12), rgba(38,195,192,0.05))',
                    marginBottom: '24px',
                  }}
                >
                  <div
                    style={{
                      fontSize: '14px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.4px',
                      color: mode === 'dark' ? '#b9d6d6' : '#2f6f6e',
                      marginBottom: '4px',
                      fontWeight: 600,
                    }}
                  >
                    Investment
                  </div>
                  <div
                    style={{
                      fontSize: '26px',
                      fontWeight: 700,
                      color: mode === 'dark' ? '#FFFFFF' : '#002b3d',
                    }}
                  >
                    {displayPrice || 'Custom pricing'}
                  </div>
                  <div
                    style={{
                      fontSize: '13px',
                      color: mode === 'dark' ? '#8f9aa3' : '#5c6b73',
                    }}
                  >
                    Preferred billing: {billingCycle}
                  </div>
                </div>

                {features.length > 0 && (
                  <div style={{ marginBottom: 'auto' }}>
                    <h5
                      style={{
                        fontSize: '15px',
                        fontWeight: 600,
                        color: mode === 'dark' ? '#E4F1F1' : '#12343b',
                        marginBottom: '12px',
                      }}
                    >
                      What’s included
                    </h5>
                    <ul
                      style={{
                        listStyle: 'none',
                        padding: 0,
                        margin: 0,
                        display: 'grid',
                        gap: '10px',
                      }}
                    >
                      {features.map((feature, index) => (
                        <li
                          key={`${feature}-${index}`}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            fontSize: '14px',
                            color: mode === 'dark' ? '#d1e4e4' : '#12343b',
                            lineHeight: 1.5,
                          }}
                        >
                          <img
                            src={Checkmark}
                            alt=""
                            style={{ width: '16px', height: '16px' }}
                          />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
          <Col xl={7} lg={7} md={12}>
            <Card
              className="h-100"
              style={{
                borderRadius: '16px',
                border: 'none',
                boxShadow: mode === 'dark'
                  ? '0 12px 24px rgba(0, 0, 0, 0.4)'
                  : '0 12px 24px rgba(38, 195, 192, 0.12)',
                backgroundColor: mode === 'dark' ? '#151515' : '#ffffff',
              }}
            >
              <Card.Body style={{ padding: '32px' }}>
                <div style={{ marginBottom: '24px' }}>
                  <h4
                    style={{
                      marginBottom: '8px',
                      color: mode === 'dark' ? '#ffffff' : '#001219',
                      fontWeight: 700,
                    }}
                  >
                    Tell us about your requirements
                  </h4>
                  <p
                    style={{
                      color: mode === 'dark' ? '#8f9aa3' : '#5c6b73',
                      marginBottom: 0,
                    }}
                  >
                    Provide a few details and our sales team will reach out with a tailored proposal.
                  </p>
                </div>
                <ContactUsForm
                  submitLabel="Contact Sales"
                  secondaryAction={
                    <Button
                      variant="outline-secondary"
                      onClick={() => navigate(APP_URLS.SUBSCRIPTION)}
                      style={{
                        borderRadius: '10px',
                        borderColor: mode === 'dark' ? '#3b3b3b' : '#d4dde1',
                        color: mode === 'dark' ? '#d4dde1' : '#3b3b3b',
                        fontWeight: 500,
                        padding: '12px 20px',
                      }}
                    >
                      Cancel
                    </Button>
                  }
                />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ContactSales;

